import React, { useState, useEffect, useRef } from 'react';
import speechService, { SpeechRecognitionResult } from '../../services/speechService';
import WebSpeechRecorder from './WebSpeechRecorder';
import { 
  MicrophoneIcon, 
  StopIcon, 
  ExclamationTriangleIcon,
  ClockIcon 
} from '@heroicons/react/24/solid';

interface SpeechToTextButtonProps {
  onTranscriptionUpdate: (text: string, isFinal: boolean) => void;
  onError?: (error: string) => void;
  baseUrl: string;
  token: string;
  disabled?: boolean;
  className?: string;
  useWebSpeechAPI?: boolean;
  useWhisperAPI?: boolean;
}

interface SpeechToTextButtonState {
  status: 'idle' | 'connecting' | 'connected' | 'recording' | 'processing' | 'error';
  interimText: string;
  finalText: string;
  error: string | null;
}

const SpeechToTextButton: React.FC<SpeechToTextButtonProps> = ({
  onTranscriptionUpdate,
  onError,
  baseUrl,
  token,
  disabled = false,
  className = '',
  useWebSpeechAPI = true,
  useWhisperAPI = false
}) => {
  const [state, setState] = useState<SpeechToTextButtonState>({
    status: 'idle',
    interimText: '',
    finalText: '',
    error: null
  });

  const pulseAnimationRef = useRef<number>();
  const [isPulsing, setIsPulsing] = useState(false);

  // Initialize speech service
  useEffect(() => {
    const initService = async () => {
      try {
        await speechService.initialize({ 
          baseUrl, 
          token, 
          useWebSpeechAPI 
        });
        
        speechService.setEventHandlers(
          handleSpeechResult,
          handleSpeechError,
          handleStatusChange
        );
      } catch (error) {
        console.error('Failed to initialize speech service:', error);
        setState(prev => ({
          ...prev,
          status: 'error',
          error: `Initialization failed: ${error}`
        }));
        onError?.(String(error));
      }
    };

    initService();

    return () => {
      speechService.disconnect();
      if (pulseAnimationRef.current) {
        cancelAnimationFrame(pulseAnimationRef.current);
      }
    };
  }, [baseUrl, token, useWebSpeechAPI]);

  // Handle speech recognition results
  const handleSpeechResult = (result: SpeechRecognitionResult) => {
    setState(prev => ({
      ...prev,
      interimText: result.isFinal ? '' : result.text,
      finalText: result.isFinal ? prev.finalText + result.text + ' ' : prev.finalText
    }));

    // Notify parent component
    const combinedText = result.isFinal 
      ? state.finalText + result.text + ' '
      : state.finalText + result.text;
    
    onTranscriptionUpdate(combinedText.trim(), result.isFinal);
  };

  // Handle speech recognition errors
  const handleSpeechError = (error: string) => {
    console.error('Speech recognition error:', error);
    setState(prev => ({
      ...prev,
      status: 'error',
      error
    }));
    onError?.(error);
    setIsPulsing(false);
  };

  // Handle status changes
  const handleStatusChange = (status: 'connecting' | 'connected' | 'recording' | 'stopped' | 'error') => {
    setState(prev => ({
      ...prev,
      status: status === 'stopped' ? 'idle' : status,
      error: status === 'error' ? prev.error : null
    }));

    if (status === 'recording') {
      setIsPulsing(true);
    } else if (status === 'stopped' || status === 'error') {
      setIsPulsing(false);
    }
  };

  // Pulse animation effect
  useEffect(() => {
    if (isPulsing) {
      const animate = () => {
        // CSS animation will handle the pulsing
        pulseAnimationRef.current = requestAnimationFrame(animate);
      };
      animate();
    } else {
      if (pulseAnimationRef.current) {
        cancelAnimationFrame(pulseAnimationRef.current);
      }
    }

    return () => {
      if (pulseAnimationRef.current) {
        cancelAnimationFrame(pulseAnimationRef.current);
      }
    };
  }, [isPulsing]);

  // Handle button press
  const handlePress = async () => {
    if (disabled || state.status === 'connecting') {
      return;
    }

    try {
      if (state.status === 'idle' || state.status === 'connected') {
        // Start recording
        setState(prev => ({ ...prev, interimText: '', finalText: '', error: null }));
        await speechService.startRecording();
      } else if (state.status === 'recording') {
        // Stop recording
        await speechService.stopRecording();
      }
    } catch (error) {
      console.error('Speech action failed:', error);
      alert(`Speech Recognition Error: ${error}`);
    }
  };

  // Get button classes based on status
  const getButtonClasses = () => {
    const baseClasses = 'w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 theme-border-primary border-2';
    
    switch (state.status) {
      case 'recording':
        return `${baseClasses} bg-red-500 text-white ${isPulsing ? 'animate-pulse' : ''} shadow-lg`;
      case 'connecting':
        return `${baseClasses} bg-healthy-green text-white opacity-75`;
      case 'error':
        return `${baseClasses} bg-red-500 text-white`;
      default:
        return `${baseClasses} bg-healthy-green hover:bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg`;
    }
  };

  // Get button icon using Heroicons
  const getButtonIcon = () => {
    switch (state.status) {
      case 'recording':
        return <StopIcon className="w-8 h-8" />;
      case 'connecting':
        return <ClockIcon className="w-8 h-8 animate-spin" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-8 h-8" />;
      default:
        return <MicrophoneIcon className="w-8 h-8" />;
    }
  };

  // Get button text
  const getButtonText = () => {
    switch (state.status) {
      case 'connecting':
        return 'Connecting...';
      case 'recording':
        return 'Recording... Click to stop';
      case 'error':
        return 'Error - Click to retry';
      default:
        return 'Click to speak';
    }
  };

  const isActive = state.status === 'recording';
  const showTranscription = state.interimText || state.finalText;

  // If using Whisper API, render the WebSpeechRecorder component instead
  if (useWhisperAPI) {
    return (
      <div className={`flex flex-col items-center space-y-4 ${className}`}>
        {/* Transcription Display */}
        {showTranscription && (
          <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-800 text-left whitespace-pre-wrap">
              {state.finalText}
              <span className="text-gray-500 italic">{state.interimText}</span>
            </p>
          </div>
        )}
        
        <WebSpeechRecorder
          onTranscriptionUpdate={onTranscriptionUpdate}
          onError={onError}
          baseUrl={baseUrl}
          token={token}
          disabled={disabled}
          className="flex-1"
        />
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center space-y-4 ${className}`}>
      {/* Transcription Display */}
      {showTranscription && (
        <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-800 text-left whitespace-pre-wrap">
            {state.finalText}
            <span className="text-gray-500 italic">{state.interimText}</span>
          </p>
        </div>
      )}

      {/* Speech Button */}
      <div className="flex flex-col items-center space-y-3">
        <div className="relative">
          <button
            type="button"
            onClick={handlePress}
            disabled={disabled || state.status === 'connecting'}
            className={getButtonClasses()}
            title={getButtonText()}
          >
            {/* Recording Indicator */}
            {isActive && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
            )}
            
            {getButtonIcon()}
          </button>
        </div>

        {/* Status Text */}
        <p className={`text-sm text-center font-medium ${
          isActive ? 'theme-text-primary' : 
          state.status === 'error' ? 'text-red-600' : 
          'theme-text-secondary'
        }`}>
          {getButtonText()}
        </p>

        {/* Error Message */}
        {state.error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-w-xs">
            <p className="text-xs text-red-600 dark:text-red-400 text-center">
              {state.error}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeechToTextButton;